import React, { useMemo } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useMediaQuery, useTheme } from '@mui/material';
import { useOnboarding } from '../OnboardingProvider';
import { TourStepCard } from './TourStepCard';
import { getTourById, TourStepDefinition } from '../tours';

/**
 * Is this step's target actually on the page right now?
 *
 * ⚠ THE FILTER IS NOT WHAT STOPS A STALL — corrected 2026-09-21 by reading the installed library.
 * react-joyride 2.9.3 in UNCONTROLLED mode (this component passes no `stepIndex`) handles a
 * missing target itself: `node_modules/react-joyride/dist/index.js` ~:1841 warns "Target not
 * mounted", emits `EVENTS.TARGET_NOT_FOUND`, and then — `if (!controlled)` — advances the index
 * past it. So the previous claim here, that a missing target leaves the overlay stuck on a step
 * nobody can advance past, was false for the version we ship.
 *
 * WHAT THE FILTER STILL BUYS, which is why it stays:
 *  - the step never renders at all, instead of flashing an overlay and a console warning on the
 *    way past — and on a FIRST-LOGIN tour that flash is the whole first impression;
 *  - `visibleSteps` is what the callback indexes for progress (`handleCallback` below). Joyride
 *    reports the index of the step IT was given, so filtering is what keeps a recorded step id
 *    pointing at the step the person actually saw;
 *  - and it makes "no walkable steps" DETECTABLE, which is the arm below: a tour whose every
 *    target has gone is ended rather than left running in provider state forever.
 *
 * This became load-bearing with the no-access screens (INV-108): the vet and tech welcome tours
 * both point at `[data-tour='nav-schedule']`, and a person without `view:appointments` has no
 * Schedule item in the sidebar. It also covers two targets that were already conditional —
 * `.ai-assistant` (absent while the kill switch is off) and `[data-tour='onboarding-pill']`
 * (absent once the checklist is done).
 *
 * The tour is started from the authenticated shell, so the sidebar, the assistant and the
 * feedback button have all rendered by the time this runs; a target that appears LATER is out of
 * scope (no tour has one today, and `tours.test.ts` pins the catalogue).
 */
const isOnPage = (selector: string): boolean => {
  try {
    return document.querySelector(selector) !== null;
  } catch {
    return false; // an invalid selector is a broken step, never a crash in the shell
  }
};

/**
 * Global tour overlay. Reads the active tour id from OnboardingProvider.state
 * (via useOnboarding), renders react-joyride with our branded step card, and
 * PATCHes tour progress back to the backend on each transition.
 */
export const TourRunner: React.FC = () => {
  const { activeTourId, endTour, recordTourProgress } = useOnboarding();
  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'));

  // Tours are unusable on narrow viewports (sidebar is collapsed, targets
  // don't exist). Skip rendering entirely rather than firing into nothing.
  const tour = activeTourId ? getTourById(activeTourId) : undefined;

  // The steps that are actually walkable, in order. Kept as DEFINITIONS as well as Joyride
  // steps because the callback below indexes into this list: Joyride reports the index of the
  // step IT was given, so reading `tour.steps[index]` after a filter would record the wrong
  // step id as progress.
  const visibleSteps: TourStepDefinition[] = useMemo(
    () => (tour ? tour.steps.filter((step) => isOnPage(step.target)) : []),
    [tour],
  );

  const joyrideSteps: Step[] = useMemo(
    () =>
      visibleSteps.map<Step>((step: TourStepDefinition) => ({
        target: step.target,
        content: step.body,
        placement: (step.placement ?? 'auto') as Step['placement'],
        disableBeacon: true,
        // Pass title via data field; TourStepCard reads it off the step.
        ...({ title: step.title } as Record<string, unknown>),
      })),
    [visibleSteps],
  );

  /**
   * A tour with nothing left to show is FINISHED, not paused.
   *
   * The zero-step case is reachable for a real person: a narrowed user whose only tour step
   * pointed at a nav item they cannot open (INV-108). Returning null left `activeTourId` set in
   * provider state for the rest of the session — no overlay, no way to dismiss it, and nothing
   * could start another tour. Ending it here releases the provider; the backend progress row is
   * deliberately untouched, because the person did not complete anything.
   */
  const noWalkableSteps = !!tour && !isTabletDown && joyrideSteps.length === 0;
  React.useEffect(() => {
    if (noWalkableSteps) endTour();
  }, [noWalkableSteps, endTour]);

  // A tour whose every target has gone is not a tour: an empty Joyride flashes an overlay with
  // nothing in it and no way out. Rendering null was only half the answer — `activeTourId` stayed
  // set in provider state, so the tour was neither shown nor finished and nothing could start
  // another one for the rest of the session. `endTour()` above closes it properly; this is just
  // the render half.
  //
  // ⚠ The tablet arm deliberately does NOT end the tour: tours are unusable at that width, but a
  // rotation or a resize makes them usable again, and consuming the tour would take it away for
  // good. Only "no walkable steps" is a terminal state.
  if (!tour || isTabletDown || joyrideSteps.length === 0) return null;

  const handleCallback = (data: CallBackProps) => {
    const { status, action, index, step, type } = data;

    if (type === 'step:after' || type === 'tour:end') {
      const stepId = visibleSteps[index]?.id ?? (step as any)?.target;
      // Cosmetic write — never break a step transition (or leak an unhandled
      // rejection) when the PATCH fails; the provider surfaces online errors.
      void recordTourProgress(tour.id, { lastStepId: stepId }).catch(() => {});
    }

    if (status === STATUS.FINISHED) {
      void recordTourProgress(tour.id, { completed: true }).catch(() => {});
      endTour();
      return;
    }

    if (status === STATUS.SKIPPED || action === 'close') {
      endTour();
      return;
    }
  };

  return (
    <Joyride
      steps={joyrideSteps}
      run
      continuous
      scrollToFirstStep
      showSkipButton
      disableScrolling={false}
      disableOverlayClose
      tooltipComponent={TourStepCard}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#8B4513',
          overlayColor: 'rgba(59,46,26,0.45)',
          zIndex: 10_000,
          arrowColor: '#8B4513',
        },
      }}
    />
  );
};
